'use client';

import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { CHANEL_BRAND_ID } from '@headless-workspace/config';
import {
    ProductDetailPricesContainer,
    useAppLocale,
    useClientData,
    useProductSet,
    usePromotionInfo,
} from '@headless-workspace/domain/common/client';
import { ProductDetailVariantValue, ProductSetItemValue } from '@headless-workspace/domain/common/server';
import { GlowClickable, GlowImage, GlowTextBodySmall, PropsWithFontFamily } from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import chanelStyles from '../chanel.module.css';
import { VariantSelectorZone } from './VariantSelectorZone';

const PRODUCT_SET_IMAGE_SIZE_PX = 80;

export type ProductSetItemProps = Pick<
    ProductSetItemValue,
    'name' | 'variants' | 'type' | 'formats' | 'currency' | 'brand'
> &
    PropsWithFontFamily & {
        isVipBrand: boolean;
        priceAlwaysBlack: boolean;
        squaredShade: boolean;
        index: number;
        enableDiscountInfo: boolean;
    };

type ProductSetItemInformationProps = Pick<
    ProductSetItemProps,
    'fontFamily' | 'isVipBrand' | 'name' | 'priceAlwaysBlack' | 'enableDiscountInfo' | 'brand'
> &
    Omit<ProductDetailVariantValue, 'id'>;

const ProductSetItemInformation: Component<ProductSetItemInformationProps> = ({
    fontFamily,
    enableDiscountInfo,
    name,
    brand,
    priceAlwaysBlack,
    currency,
    image,
    price,
    priorPrice,
    originalPrice,
    pricePerUnit,
    promotion,
    discountPercentage,
    url,
}) => {
    const t = useTranslations('Discover.Pdp.information.price');
    const { getClientFlags } = useClientData();
    const locale = useAppLocale();
    const promotionInformation = usePromotionInfo(promotion);

    const isPriceLegalMentionEnabled = getClientFlags('isPriceExceptionLegalMentionsDisplayed');
    const itemImage = (
        <GlowImage
            image={image}
            height={PRODUCT_SET_IMAGE_SIZE_PX}
            width={PRODUCT_SET_IMAGE_SIZE_PX}
            className={'min-w-[5rem]'}
        />
    );
    const isChanelBrand = brand.id === CHANEL_BRAND_ID;

    return (
        <div className={'flex flex-row gap-1 items-start self-stretch'}>
            {url ? (
                <GlowClickable
                    href={url}
                    content={{
                        icon: itemImage,
                    }}
                />
            ) : (
                itemImage
            )}

            <div className={'flex flex-col gap-0.5'}>
                <div className={'flex flex-col gap-0.25'}>
                    <GlowClickable
                        href={brand.url}
                        content={{
                            labelElement: (
                                <GlowTextBodySmall
                                    className={clsx('line-clamp-2', isChanelBrand && chanelStyles.chanelBrandText)}
                                    text={brand.name}
                                    fontFamily={fontFamily}
                                    fontWeight={'bold'}
                                    TagName={'span'}
                                />
                            ),
                        }}
                    />
                    <GlowTextBodySmall fontFamily={fontFamily} text={name} className={'line-clamp-2'} />
                </div>
                <ProductDetailPricesContainer
                    variant={'small'}
                    priorPrice={priorPrice}
                    price={price}
                    currency={currency}
                    locale={locale}
                    enableDiscountInfo={enableDiscountInfo}
                    pricePerUnit={pricePerUnit}
                    fontFamily={fontFamily}
                    priceAlwaysBlack={priceAlwaysBlack}
                    originalPrice={originalPrice}
                    promotionInformation={promotionInformation}
                    discountPercentageLabel={
                        discountPercentage
                            ? t('label.discountPercentage', {
                                  discountPercentage,
                              })
                            : undefined
                    }
                    promotion={promotion}
                    isPriceLegalMentionEnabled={isPriceLegalMentionEnabled}
                />
            </div>
        </div>
    );
};

export const ProductSetItem: Component<ProductSetItemProps> = ({
    name,
    brand,
    variants,
    type,
    formats,
    currency,
    priceAlwaysBlack,
    fontFamily,
    isVipBrand,
    squaredShade,
    index,
    enableDiscountInfo,
}) => {
    const locale = useAppLocale();
    const { getClientFlags } = useClientData();

    const isPriceLegalMentionEnabled = getClientFlags('isPriceExceptionLegalMentionsDisplayed');
    const { getSelectedProductSetVariant, setProductSetVariant, setProductSetVariantPreview } = useProductSet();
    const selectedVariant = getSelectedProductSetVariant(index);

    if (!selectedVariant) {
        return null;
    }

    const { id, ...selectedVariantProps } = selectedVariant;

    return (
        <div className={'flex flex-col gap-1'}>
            <ProductSetItemInformation
                {...selectedVariantProps}
                name={name}
                brand={brand}
                priceAlwaysBlack={priceAlwaysBlack}
                fontFamily={fontFamily}
                isVipBrand={isVipBrand}
                enableDiscountInfo={enableDiscountInfo}
            />
            <VariantSelectorZone
                productName={name}
                fontFamily={fontFamily}
                formats={formats}
                currency={currency}
                locale={locale}
                type={type}
                variants={variants}
                squaredShade={squaredShade}
                isVipBrand={isVipBrand}
                priceAlwaysBlack={priceAlwaysBlack}
                onVariantSelected={setProductSetVariant(index)}
                selectedVariantId={id}
                setHoveredVariant={setProductSetVariantPreview(index)}
                isPriceLegalMentionEnabled={isPriceLegalMentionEnabled}
                enableDiscountInfo={enableDiscountInfo}
            />
        </div>
    );
};
